import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { CheckCircle, Cancel, LinkedIn, Warning } from "@mui/icons-material";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-hot-toast";
import { API_SERVICE_BACKEND, SERVER_DOMAIN } from "src/config";

const linkedInMigrationHttp = axios.create();
linkedInMigrationHttp.interceptors.request.use((config) => {
  const url = config.url || "";
  const isGatewayDomain =
    url.includes(SERVER_DOMAIN) || url.includes("localhost");
  if (isGatewayDomain) {
    const token = Cookies.get("b2b_at");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
  } else {
    delete config.headers.Authorization;
  }
  return config;
});

function migrationSecretBodyFields(secret) {
  return { token: secret, migrationToken: secret };
}

const LinkedinMigrationApprovalPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("pending");
  const [errorMessage, setErrorMessage] = useState("");
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [denyLoading, setDenyLoading] = useState(false);
  const [denyDialogError, setDenyDialogError] = useState("");
  /** Accept flow: null | "blocked" (active campaigns) | "confirm" (safe to migrate) | "check_error" */
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveDialogVariant, setApproveDialogVariant] = useState(null);
  const [approveCheckLoading, setApproveCheckLoading] = useState(false);
  const [approveSubmitLoading, setApproveSubmitLoading] = useState(false);
  const [approveDialogCampaignNames, setApproveDialogCampaignNames] = useState([]);
  const [approveDialogCheckMessage, setApproveDialogCheckMessage] = useState("");
  const [approveDialogError, setApproveDialogError] = useState("");
  const [linkValidationStatus, setLinkValidationStatus] = useState(() =>
    searchParams.get("migrationToken") || searchParams.get("token") ? "loading" : "invalid"
  );
  const [linkValidationMessage, setLinkValidationMessage] = useState(() =>
    searchParams.get("migrationToken") || searchParams.get("token")
      ? ""
      : "Missing security token. Open the full link from your email."
  );
  const [validatedAccountId, setValidatedAccountId] = useState("");
  const [validatedConnectionId, setValidatedConnectionId] = useState("");

  const linkedinAccountName =
    searchParams.get("linkedinAccountName") ||
    searchParams.get("accountName") ||
    searchParams.get("senderEmail");
  const previousOwner =
    searchParams.get("previousOwner") || searchParams.get("previousOwnerEmail");
  const newOwner =
    searchParams.get("newOwner") ||
    searchParams.get("newOwnerEmail") ||
    searchParams.get("userEmail");
  const accountId = searchParams.get("accountId") || searchParams.get("account_id");
  const connectionId = searchParams.get("connectionId") || searchParams.get("connection_id");
  const migrationToken =
    searchParams.get("migrationToken") || searchParams.get("token") || "";

  const resolvedAccountId = (validatedAccountId || accountId || "").trim();
  const resolvedConnectionId = (validatedConnectionId || connectionId || "").trim();

  useEffect(() => {
    if (!migrationToken) {
      setLinkValidationStatus("invalid");
      setLinkValidationMessage(
        "Missing security token. Open the full link from your email."
      );
      setValidatedAccountId("");
      setValidatedConnectionId("");
      return;
    }

    let cancelled = false;
    setLinkValidationStatus("loading");
    setLinkValidationMessage("");

    (async () => {
      try {
        const res = await linkedInMigrationHttp.post(
          `${API_SERVICE_BACKEND}/unipile/linkedin/migration-link/validate`,
          {
            ...migrationSecretBodyFields(migrationToken),
            ...(accountId ? { accountId } : {}),
            ...(connectionId ? { connectionId } : {}),
          }
        );
        const data = res?.data ?? {};
        if (cancelled) return;

        if (data.success === false) {
          setLinkValidationStatus("invalid");
          setLinkValidationMessage(
            (typeof data.message === "string" && data.message) ||
              "Could not validate migration link."
          );
          setValidatedAccountId("");
          setValidatedConnectionId("");
          return;
        }

        if (data.valid !== true) {
          setLinkValidationStatus("invalid");
          setLinkValidationMessage(
            (typeof data.message === "string" && data.message) ||
              "Invalid migration link."
          );
          setValidatedAccountId("");
          setValidatedConnectionId("");
          return;
        }

        const d = data.data ?? {};
        const aid =
          typeof d.accountId === "string" ? d.accountId.trim() : "";
        const cid =
          typeof d.connectionId === "string" ? d.connectionId.trim() : "";
        setValidatedAccountId(aid);
        setValidatedConnectionId(cid);
        setLinkValidationStatus("valid");
      } catch (err) {
        if (cancelled) return;
        setLinkValidationStatus("invalid");
        setLinkValidationMessage(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            "Could not validate migration link."
        );
        setValidatedAccountId("");
        setValidatedConnectionId("");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [migrationToken, accountId, connectionId]);

  const buildCampaignCheckPayload = () => {
    const payload = {
      accountId: resolvedAccountId,
      ...migrationSecretBodyFields(migrationToken),
    };
    if (resolvedConnectionId) {
      payload.connectionId = resolvedConnectionId;
    }
    return payload;
  };

  const resetApproveDialog = () => {
    setApproveDialogOpen(false);
    setApproveDialogVariant(null);
    setApproveDialogCampaignNames([]);
    setApproveDialogCheckMessage("");
    setApproveDialogError("");
  };

  const handleApproveDialogClose = () => {
    if (approveCheckLoading || approveSubmitLoading) return;
    resetApproveDialog();
  };

  const submitMigrationApprove = async () => {
    setApproveSubmitLoading(true);
    setErrorMessage("");
    setApproveDialogError("");
    try {
      const res = await linkedInMigrationHttp.post(
        `${API_SERVICE_BACKEND}/unipile/linkedin/migration-approve`,
        {
          accountId: resolvedAccountId,
          accountName: linkedinAccountName,
          previousOwner,
          userEmail: newOwner,
          approved: true,
          status: "approved",
          ...(resolvedConnectionId ? { connectionId: resolvedConnectionId } : {}),
          ...migrationSecretBodyFields(migrationToken),
        }
      );
      const data = res?.data ?? {};
      if (data.success === false) {
        const msg =
          (typeof data.message === "string" && data.message) ||
          (typeof data.error === "string" && data.error) ||
          "LinkedIn migration could not be completed";
        setApproveDialogError(msg);
        toast.error(msg);
        return;
      }
      resetApproveDialog();
      setStatus("success");
      toast.success("LinkedIn migration completed successfully!");
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to approve LinkedIn migration";
      setApproveDialogError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setApproveSubmitLoading(false);
    }
  };

  const handleAcceptClick = async () => {
    if (!migrationToken || !resolvedAccountId) {
      toast.error("Missing or invalid migration link. Open the link from your email.");
      return;
    }

    setApproveCheckLoading(true);
    setApproveDialogError("");
    setApproveDialogCheckMessage("");
    setApproveDialogCampaignNames([]);
    try {
      const res = await linkedInMigrationHttp.post(
        `${API_SERVICE_BACKEND}/unipile/linkedin/migration-active-campaigns-check`,
        buildCampaignCheckPayload()
      );
      const data = res?.data ?? {};
      if (data.success === false) {
        const msg =
          (typeof data.message === "string" && data.message) ||
          (typeof data.error === "string" && data.error) ||
          "Could not verify campaigns for this account.";
        setApproveDialogVariant("check_error");
        setApproveDialogError(msg);
        setApproveDialogOpen(true);
        toast.error(msg);
        return;
      }

      const namesFromApi = Array.isArray(data.campaignNames)
        ? data.campaignNames.filter((n) => typeof n === "string" && n.trim() !== "")
        : [];
      const fromActive =
        Array.isArray(data.activeCampaigns) && data.activeCampaigns.length > 0
          ? data.activeCampaigns
              .map((c) => (typeof c?.name === "string" ? c.name.trim() : ""))
              .filter(Boolean)
          : [];
      const campaignNames =
        namesFromApi.length > 0 ? namesFromApi : fromActive;

      const hasActive = data.hasActiveCampaigns === true;
      setApproveDialogCheckMessage(
        typeof data.message === "string" ? data.message : ""
      );
      setApproveDialogCampaignNames(campaignNames);

      if (hasActive) {
        setApproveDialogVariant("blocked");
      } else {
        setApproveDialogVariant("confirm");
      }
      setApproveDialogOpen(true);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Could not verify campaigns for this account.";
      setApproveDialogVariant("check_error");
      setApproveDialogError(msg);
      setApproveDialogOpen(true);
      toast.error(msg);
    } finally {
      setApproveCheckLoading(false);
    }
  };

  const handleDenyClick = () => {
    setDenyDialogError("");
    setDenyDialogOpen(true);
  };

  const handleDenyDialogClose = () => {
    if (denyLoading) return;
    setDenyDialogOpen(false);
    setDenyDialogError("");
  };

  const handleConfirmDenyMigration = async () => {
    if (!migrationToken || !resolvedAccountId) {
      toast.error("Missing or invalid migration link. Open the link from your email.");
      return;
    }
    setDenyLoading(true);
    setDenyDialogError("");
    try {
      const denyPayload = {
        accountId: resolvedAccountId,
        ...migrationSecretBodyFields(migrationToken),
        ...(resolvedConnectionId ? { connectionId: resolvedConnectionId } : {}),
      };
      const res = await linkedInMigrationHttp.post(
        `${API_SERVICE_BACKEND}/unipile/linkedin/migration-deny`,
        denyPayload
      );
      const data = res?.data ?? {};
      if (data.success === false) {
        const msg =
          (typeof data.message === "string" && data.message) ||
          (typeof data.error === "string" && data.error) ||
          "Could not refuse this migration request.";
        setDenyDialogError(msg);
        toast.error(msg);
        return;
      }
      setDenyDialogOpen(false);
      setStatus("denied");
      toast.success("Migration refused. The pending connection has been removed.");
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to refuse LinkedIn migration";
      setDenyDialogError(msg);
      toast.error(msg);
    } finally {
      setDenyLoading(false);
    }
  };

  if (!migrationToken) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          padding: 2,
        }}
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Alert severity="error">{linkValidationMessage}</Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (linkValidationStatus === "loading") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          padding: 2,
        }}
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Verifying migration link…
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (linkValidationStatus === "invalid") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          padding: 2,
        }}
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Alert severity="error">
              {linkValidationMessage ||
                "This migration link is invalid, expired, or has already been used."}
            </Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (!linkedinAccountName || !previousOwner || !newOwner) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          padding: 2,
        }}
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Alert severity="warning">
              This migration link is valid, but the URL is missing some details (account name or
              owner emails). Use the complete link from your email.
            </Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (!resolvedAccountId) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          padding: 2,
        }}
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Alert severity="error">
              Migration link is valid but no account id was returned. Contact support or request a
              new migration email.
            </Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{ borderRadius: 3, padding: 4, textAlign: "center", background: "white" }}
        >
          {status === "pending" && (
            <>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "#0A66C2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                }}
              >
                <LinkedIn sx={{ fontSize: 34, color: "white" }} />
              </Box>

              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: "#1a1a1a" }}>
                LinkedIn Migration Approval
              </Typography>

              <Box
                sx={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                  padding: 2,
                  mb: 3,
                  textAlign: "left",
                }}
              >
                <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                  LinkedIn Account
                </Typography>
                <Typography variant="body1" sx={{ color: "#1a1a1a", fontWeight: 600 }}>
                  {linkedinAccountName}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1.5,
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#6b7280" }}>
                    Previous Owner
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#1a1a1a", fontWeight: 500 }}>
                    {previousOwner}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1.5,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#6b7280" }}>
                    New Owner
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#1a1a1a", fontWeight: 500 }}>
                    {newOwner}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  background: "#fef3c7",
                  border: "1px solid #fbbf24",
                  borderRadius: 2,
                  padding: 2,
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Warning sx={{ color: "#92400e", fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: "#92400e", textAlign: "left" }}>
                  Approving this will migrate LinkedIn account ownership to the new owner.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDenyClick}
                  disabled={approveCheckLoading || approveSubmitLoading || denyLoading}
                  sx={{ flex: 1, py: 1.5 }}
                >
                  Deny
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAcceptClick}
                  disabled={
                    approveCheckLoading || approveSubmitLoading || denyLoading
                  }
                  startIcon={
                    approveCheckLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CheckCircle />
                    )
                  }
                  sx={{ flex: 1, py: 1.5 }}
                >
                  {approveCheckLoading ? "Checking…" : "Accept"}
                </Button>
              </Box>
            </>
          )}

          {status === "success" && (
            <>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "#10b981",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                }}
              >
                <CheckCircle sx={{ fontSize: 40, color: "white" }} />
              </Box>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: "#1a1a1a" }}>
                LinkedIn Migration Success
              </Typography>
              <Typography variant="body1" sx={{ color: "#6b7280", mb: 3 }}>
                LinkedIn account has been successfully migrated to {newOwner}
              </Typography>
            </>
          )}

          {status === "denied" && (
            <>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                }}
              >
                <Cancel sx={{ fontSize: 40, color: "white" }} />
              </Box>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: "#1a1a1a" }}>
                Migration Request Denied
              </Typography>
              <Typography variant="body1" sx={{ color: "#6b7280", mb: 3 }}>
                The LinkedIn migration request has been denied.
              </Typography>
            </>
          )}

          {status === "error" && (
            <>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                }}
              >
                <Cancel sx={{ fontSize: 40, color: "white" }} />
              </Box>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: "#1a1a1a" }}>
                Migration Failed
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
                {errorMessage || "An error occurred during LinkedIn migration"}
              </Alert>
            </>
          )}
        </Paper>

        <Dialog
          open={denyDialogOpen}
          onClose={handleDenyDialogClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Refuse LinkedIn migration?</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Are you sure you want to refuse migrating the LinkedIn account{" "}
              <strong>{linkedinAccountName}</strong>? The new owner will not receive this
              connection, and the pending request will be discarded.
            </Typography>
            {denyDialogError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {denyDialogError}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleDenyDialogClose} disabled={denyLoading} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDenyMigration}
              disabled={denyLoading}
              startIcon={
                denyLoading ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              {denyLoading ? "Confirming…" : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={approveDialogOpen}
          onClose={handleApproveDialogClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {approveDialogVariant === "blocked" &&
              "This account is attached to active campaigns"}
            {approveDialogVariant === "confirm" && "Confirm LinkedIn migration"}
            {approveDialogVariant === "check_error" && "Could not verify campaigns"}
          </DialogTitle>
          <DialogContent>
            {approveDialogVariant === "blocked" && (
              <>
                <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
                  {approveDialogCheckMessage ||
                    "This LinkedIn account is attached to active campaigns."}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                  To migrate, pause or archive each campaign below (so it is no longer active),
                  then click <strong>Accept</strong> again on the previous screen.
                </Typography>
                {approveDialogCampaignNames.length > 0 ? (
                  <List dense disablePadding sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                    {approveDialogCampaignNames.map((name, idx) => (
                      <ListItem key={`${name}-${idx}`} sx={{ py: 0.5 }}>
                        <ListItemText primary={name} primaryTypographyProps={{ fontWeight: 500 }} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Active campaigns were reported but no names were returned. Check your
                    campaigns list in the app, pause any using this account, then try again.
                  </Alert>
                )}
              </>
            )}

            {approveDialogVariant === "confirm" && (
              <>
                <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
                  {approveDialogCheckMessage ||
                    "No active campaigns are using this LinkedIn account. You can safely migrate."}
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  Are you sure you want to migrate the LinkedIn account{" "}
                  <strong>{linkedinAccountName}</strong> to <strong>{newOwner}</strong>? This
                  action will complete the migration for the new owner.
                </Typography>
                {approveDialogError ? (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {approveDialogError}
                  </Alert>
                ) : null}
              </>
            )}

            {approveDialogVariant === "check_error" && (
              <Alert severity="error">{approveDialogError}</Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            {approveDialogVariant === "blocked" && (
              <Button variant="contained" onClick={handleApproveDialogClose} color="primary">
                Close
              </Button>
            )}
            {approveDialogVariant === "check_error" && (
              <Button variant="contained" onClick={handleApproveDialogClose} color="primary">
                Close
              </Button>
            )}
            {approveDialogVariant === "confirm" && (
              <>
                <Button
                  onClick={handleApproveDialogClose}
                  disabled={approveSubmitLoading}
                  color="inherit"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={submitMigrationApprove}
                  disabled={approveSubmitLoading}
                  startIcon={
                    approveSubmitLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {approveSubmitLoading ? "Confirming…" : "Confirm"}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default LinkedinMigrationApprovalPage;
